/**
 * Resource Plan lifecycle — an explicit transition map (docs/specs/resource-plan.md RP-9.1).
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * A DELIBERATE DEVIATION FROM REPO PRECEDENT — FLAG THIS IN REVIEW
 * ──────────────────────────────────────────────────────────────────────────────
 * No state machine exists anywhere in this codebase. The Staffing Request lifecycle is
 * ad-hoc inline guards (four statuses, one decision), and that works at that size. The
 * plan lifecycle has TEN statuses (§4.6) reached from the plan header, the approval
 * workflow, the Handover workflow and staffing — spread across four delivery slices.
 * Inline guards would rot: the M5 author cannot see the M2 author's `if`, and the first
 * missed one lets a Lost plan be handed over.
 *
 * So the legal edges live here, in one table, with the fields each edge requires. This is
 * a considered departure, not an oversight.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * M2 IMPLEMENTS FOUR STATUSES. THE REST ARE ABSENT, NOT STUBBED.
 * ──────────────────────────────────────────────────────────────────────────────
 * Draft → Proposed → Won → Lost. `SoftCommitted`, `HandedOver` and `Staffed` are M5;
 * `Active`, `Closed` and `Superseded` are [SYSTEM] transitions driven by the Staffing
 * Request commit and the Solution's own dates.
 *
 * They exist in the `resource_plan_status` enum — declaring the full vocabulary in one
 * CREATE TYPE avoids a later `ALTER TYPE ADD VALUE` that Postgres would then refuse to
 * let the same migration index on (55P04). But they are NOT edges in this map. An
 * unimplemented transition is refused with a message naming the slice that will add it,
 * which is honest; a stub that silently succeeds is not.
 *
 * The approval gate (RP-17.1) that holds a below-floor plan in Draft is M4. M2's
 * Draft → Proposed is unconditional, and `requiresApprovalCheck` below marks the edge
 * where that gate will attach so M4 does not have to rediscover it.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * THIS MAP IS THE AUTHORITY FOR THE *GUIDED* ROUTE, NOT THE ONLY ROUTE
 * ──────────────────────────────────────────────────────────────────────────────
 * `src/api/plan-lifecycle.ts` also carries a deliberate escape hatch: a status the map
 * has no edge for can be SET directly by a holder of `manageResourcePlan`, with a reason,
 * an audit row marked `via: "override"`, and no edge check. It exists because six of the
 * ten statuses have no inbound edge at all until M5/M6, and a plan that is Won is
 * otherwise unreachable.
 *
 * Two properties survive that hatch, and both are load-bearing:
 *
 *   1. `Draft → Proposed` IS an edge, so the approval gate is the only way out of Draft
 *      into Proposed. The override can never be used to skip it.
 *   2. `REQUIRED_FIELDS_BY_STATUS` is keyed on the TARGET STATUS rather than on the edge,
 *      so a direct set to Won or Lost is held to the same fields — the same two the
 *      `resource_plan` CHECK constraints enforce, which do not care how the row got there.
 *
 * Do NOT add edges here to make the override unnecessary. M5 owns this map's next edges,
 * and an edge carries side effects (a Handover record, staffing the Pod) that a label
 * change deliberately does not.
 */

import { HttpError } from "./http-errors";
import { planStatusLabel } from "./plan-status-labels";

/** Every value of `resource_plan_status`. */
export const RESOURCE_PLAN_STATUSES = [
  "Draft",
  "Proposed",
  "SoftCommitted",
  "Won",
  "HandedOver",
  "Staffed",
  "Active",
  "Closed",
  "Lost",
  "Superseded",
  "Locked",
] as const;

export type ResourcePlanStatus = (typeof RESOURCE_PLAN_STATUSES)[number];

/**
 * Statuses a plan can still carry in history but can no longer be put in. `Staffed` was
 * retired 2026-10-14 — the plan's Resourcing status says it — and the
 * retire_staffed_plan_status migration moved every live plan off it. It stays in the type
 * because frozen versions and audit rows recorded it.
 */
export const LEGACY_PLAN_STATUSES = ["Staffed"] as const satisfies readonly ResourcePlanStatus[];

export function isLegacyPlanStatus(status: ResourcePlanStatus): boolean {
  return (LEGACY_PLAN_STATUSES as readonly ResourcePlanStatus[]).includes(status);
}

/** Every status a plan can be put in — the status picker and the list filter. */
export const SELECTABLE_PLAN_STATUSES: readonly ResourcePlanStatus[] =
  RESOURCE_PLAN_STATUSES.filter((s) => !isLegacyPlanStatus(s));

/** The four M2 implements. Everything else is a later slice. */
export const M2_STATUSES = [
  "Draft",
  "Proposed",
  "Won",
  "Lost",
] as const satisfies readonly ResourcePlanStatus[];

/** Which slice owns the statuses M2 does not, so a refusal can say so. */
const SLICE_BY_STATUS: Partial<Record<ResourcePlanStatus, string>> = {
  SoftCommitted: "M5 (Soft Commit and Handover)",
  HandedOver: "M5 (Soft Commit and Handover)",
  Staffed: "M5 (Soft Commit and Handover)",
  Active: "M5 — set by [SYSTEM] when the Pod is staffed",
  Closed: "M6 — set by [SYSTEM] when the Solution closes",
  Superseded: "M5 — set by [SYSTEM] when a later plan becomes Active",
};

/** A field a transition cannot happen without. */
export type RequiredPlanField = "wonDate" | "sowReference" | "lostReason";

export interface PlanTransition {
  to: ResourcePlanStatus;
  /** Fields that must be present and non-blank for this edge (RP-9.1). */
  requires: readonly RequiredPlanField[];
  /**
   * RP-17.1 attaches the approval gate to this edge in M4: if the margin of record is
   * below floor, or any line is below cost, the transition instead creates an Approval
   * Request and holds the plan in Draft (Awaiting Approval). M2 does not gate.
   */
  requiresApprovalCheck?: true;
  /** Why this edge exists, for the error message and for the reader. */
  note: string;
}

/**
 * Which fields a STATUS requires, however it is reached.
 *
 * Keyed on the target rather than on the edge because `resource_plan` enforces exactly
 * these two rules as CHECK constraints — `resource_plan_won_requires_date_and_sow` and
 * `resource_plan_lost_requires_reason` — and a CHECK does not care which edge, or whether
 * any edge at all, put the row there. One definition, read by both the transition map
 * below and the direct-set path in src/api/plan-lifecycle.ts, so the two cannot drift into
 * disagreeing about what a Won plan needs.
 */
export const REQUIRED_FIELDS_BY_STATUS: Partial<
  Record<ResourcePlanStatus, readonly RequiredPlanField[]>
> = {
  Won: ["wonDate", "sowReference"],
  Lost: ["lostReason"],
};

export function requiredFieldsForStatus(to: ResourcePlanStatus): readonly RequiredPlanField[] {
  return REQUIRED_FIELDS_BY_STATUS[to] ?? [];
}

/**
 * The legal edges, `from → to[]`.
 *
 * Only M2's four statuses appear as `from` keys with outbound edges. A plan that somehow
 * reaches a later status cannot be moved by this module at all, which is the correct
 * behaviour until M5 teaches it how.
 */
export const PLAN_TRANSITIONS: Record<ResourcePlanStatus, readonly PlanTransition[]> = {
  Draft: [
    {
      to: "Proposed",
      requires: [],
      requiresApprovalCheck: true,
      note: "Sent to the client. M4 freezes a Sent version here and gates on the margin floor.",
    },
    // Draft → Lost is deliberately absent. §4.6: Lost comes "from Proposed or Soft
    // Committed". A Draft that never went out was not lost, it was abandoned — deleting
    // it is the honest action, and only Draft can be deleted.
  ],
  Proposed: [
    {
      to: "Won",
      requires: requiredFieldsForStatus("Won"),
      note: "Signed. Proposed → Won directly is permitted — a deal that never needed staging.",
    },
    {
      to: "Lost",
      requires: requiredFieldsForStatus("Lost"),
      note: "Excluded from Aggregate Planned Demand (RP-9.3).",
    },
    {
      to: "Draft",
      requires: [],
      note: "Reopened to be reworked. M4 will create a new Draft version rather than editing the frozen one.",
    },
  ],
  Won: [
    {
      to: "Lost",
      requires: requiredFieldsForStatus("Lost"),
      note: "A signed deal that fell through before Handover.",
    },
    // Won → HandedOver is M5: it is a [SYSTEM] transition after [FIN] approval that
    // writes the Handover record (RP-19.1). How Handover staffs the Pod is to be
    // re-specified now that a Staffing Request is a single Pod change.
  ],
  Lost: [
    {
      to: "Proposed",
      requires: [],
      note: "Reopened — a deal that came back. Clears the Lost reason.",
    },
  ],

  // Later slices. Empty rather than stubbed — see the header.
  SoftCommitted: [],
  HandedOver: [],
  Staffed: [],
  Active: [],
  Closed: [],
  Superseded: [],
  // Set and cleared only from the status picker (the override path). No required fields.
  Locked: [],
};

/**
 * The edge `from → to`, or `undefined` if the map has none.
 *
 * `undefined` is the signal the direct-set path in src/api/plan-lifecycle.ts branches on,
 * so it is a lookup rather than a throw: "there is no edge" is a routing fact there and an
 * error only on the guided route.
 */
export function findTransition(
  from: ResourcePlanStatus,
  to: ResourcePlanStatus,
): PlanTransition | undefined {
  return PLAN_TRANSITIONS[from].find((t) => t.to === to);
}

/** Statuses excluded from Aggregate Planned Demand by default (RP-9.3). */
export const DEMAND_EXCLUDED_STATUSES = [
  "Lost",
  "Closed",
  "Superseded",
] as const satisfies readonly ResourcePlanStatus[];

/**
 * Only a Draft can be DELETED. Anything that has been out is history, and deleting it would
 * take its frozen versions with it. Editing is a different question — see `planEditMode`.
 */
export function isPlanEditable(status: ResourcePlanStatus): boolean {
  return status === "Draft";
}

/**
 * Won and every status a won plan moves on to. Lost and Superseded are not here: a Lost
 * plan was never won, and a Superseded one has been replaced by a later plan.
 */
export const AFTER_WON_STATUSES = [
  "Won",
  "HandedOver",
  // Legacy — no live plan holds it (see LEGACY_PLAN_STATUSES). Kept so a row that somehow
  // does still reads as after Won.
  "Staffed",
  "Active",
  "Closed",
] as const satisfies readonly ResourcePlanStatus[];

/**
 * Whether the plan is Won or later — the window in which versions are committed ("Commit
 * version", src/api/plan-versions.ts). Locked is not in it: nothing on a Locked plan
 * changes, so there is nothing to commit. Moving the plan back before Won — to Draft,
 * Proposed or Soft Committed — turns versioning off again. It does NOT decide whether the
 * plan can be edited: every plan can, in every status (see `planEditMode`).
 */
export function isPlanEditableAfterWon(status: ResourcePlanStatus): boolean {
  return (AFTER_WON_STATUSES as readonly ResourcePlanStatus[]).includes(status);
}

/**
 * How an edit to a plan is recorded. A plan is editable in EVERY status
 * (specs/workspaces/fix-allow-editing-plans-incl-adding-roles-in-all-zsun.md R2, R4) — the
 * status only decides the bookkeeping:
 *
 *   • "draft"   — a Draft. Edited freely, as always.
 *   • "tracked" — anything else. The edit is listed in Plan History and the plan is marked
 *                 as having changed since its last frozen version, so the next freeze or
 *                 commit captures it. An edit never changes the plan's status (R3).
 *   • "locked"  — a Locked plan (added 2026-10-14). Nothing on it may change until its
 *                 status is changed back; src/api/plan-access.ts `editMode` refuses.
 */
export type PlanEditMode = "draft" | "tracked" | "locked";

export function planEditMode(status: ResourcePlanStatus): PlanEditMode {
  if (status === "Draft") return "draft";
  if (status === "Locked") return "locked";
  return "tracked";
}

/**
 * Statuses in which an edit re-runs the margin-floor approval gate (2026-10-14): a plan
 * that has been sent and is still live. Draft has its own gate, on Propose; Lost and
 * Superseded are no longer in play; a Locked plan cannot be edited at all.
 */
export function editRechecksMargin(status: ResourcePlanStatus): boolean {
  return status !== "Draft" && status !== "Lost" && status !== "Superseded" && status !== "Locked";
}

/**
 * How far along a plan is, for everything that depends on that rather than on whether it
 * can be edited — Actuals, guidance, Change Orders. A Locked plan answers with the status
 * it was locked from, because locking changes nothing about where the deal stands.
 */
export function effectivePlanStatus(plan: {
  status: string;
  lockedFromStatus?: string | null;
}): string {
  return plan.status === "Locked" ? (plan.lockedFromStatus ?? plan.status) : plan.status;
}

/**
 * RP-8.4: a Change Order is raised against a won plan. Won, plus HandedOver — where the
 * retired Staffed plans went (2026-10-14), so they keep the Change Orders they had — and
 * legacy Staffed. A Locked plan answers with the status it was locked from.
 */
export function changeOrderAllowed(plan: {
  status: string;
  lockedFromStatus?: string | null;
}): boolean {
  const status = effectivePlanStatus(plan);
  return status === "Won" || status === "HandedOver" || status === "Staffed";
}

export function isM2Status(status: ResourcePlanStatus): boolean {
  return (M2_STATUSES as readonly ResourcePlanStatus[]).includes(status);
}

/** Runtime guard — `resource_plan.status` arrives as a string at the boundary. */
export function isResourcePlanStatus(value: string): value is ResourcePlanStatus {
  return (RESOURCE_PLAN_STATUSES as readonly string[]).includes(value);
}

export interface TransitionFields {
  wonDate?: string | null;
  sowReference?: string | null;
  lostReason?: string | null;
}

/**
 * Assert a transition is legal and its required fields are present.
 *
 * Throws `HttpError(422)` with a message that names what is missing or why the edge does
 * not exist. The CHECK constraints on `resource_plan` enforce the same required fields at
 * the storage layer, so a direct SQL write cannot produce a Won plan with no SOW either —
 * this is the layer that produces a readable refusal.
 */
export function assertTransitionAllowed(input: {
  from: ResourcePlanStatus;
  to: ResourcePlanStatus;
  fields: TransitionFields;
}): PlanTransition {
  const { from, to, fields } = input;

  if (from === to) {
    throw new HttpError(422, `This plan is already ${planStatusLabel(from)}.`);
  }

  const edge = PLAN_TRANSITIONS[from].find((t) => t.to === to);
  if (!edge) {
    // Distinguish "not built yet" from "not a legal move" — they need different actions
    // from the reader.
    const slice = SLICE_BY_STATUS[to];
    if (slice !== undefined) {
      throw new HttpError(422, `Moving a plan to ${planStatusLabel(to)} arrives in ${slice}.`);
    }
    const legal = PLAN_TRANSITIONS[from].map((t) => t.to);
    if (legal.length === 0) {
      const fromSlice = SLICE_BY_STATUS[from];
      throw new HttpError(
        422,
        fromSlice === undefined
          ? `A plan that is ${planStatusLabel(from)} cannot be moved.`
          : `Moving a plan out of ${planStatusLabel(from)} arrives in ${fromSlice}.`,
      );
    }
    throw new HttpError(
      422,
      `This plan is ${planStatusLabel(from)} and cannot become ${planStatusLabel(to)}. It can become ${joinWithOr(legal.map(planStatusLabel))}.`,
    );
  }

  assertStatusFieldsPresent({ to, fields });

  return edge;
}

/**
 * Assert the fields `to` cannot exist without are present and non-blank.
 *
 * Called by `assertTransitionAllowed` for the guided route and directly by the override in
 * src/api/plan-lifecycle.ts. The `resource_plan` CHECKs enforce the same thing at the
 * storage layer; this is the layer that produces a readable refusal instead of a raw
 * constraint violation.
 */
export function assertStatusFieldsPresent(input: {
  to: ResourcePlanStatus;
  fields: TransitionFields;
}): void {
  const { to, fields } = input;

  const missing = requiredFieldsForStatus(to).filter((field) => {
    const value = fields[field];
    return value === undefined || value === null || value.trim() === "";
  });
  if (missing.length > 0) {
    throw new HttpError(
      422,
      `Moving this plan to ${planStatusLabel(to)} needs ${joinWithAnd(missing.map(describeField))}.`,
    );
  }
}

function describeField(field: RequiredPlanField): string {
  switch (field) {
    case "wonDate":
      return "a Won date";
    case "sowReference":
      return "an SOW reference";
    case "lostReason":
      return "a reason";
  }
}

function joinWithAnd(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

function joinWithOr(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} or ${items.at(-1)}`;
}
